import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, Check, ArrowRight, ArrowLeft, Loader2, FileText, Upload, X, AlertCircle } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MAX_MB } from "@/lib/archivos";
import { cargarRegimenes, buscarRegimen } from "@/lib/regimenes";
import { resolveTurnstileSiteKey } from "@/lib/turnstile";
import { VoraLogo } from "@/components/brand/VoraLogo";
import { toast } from "sonner";

const TURNSTILE_SITE_KEY = resolveTurnstileSiteKey();

// Wizard de 5 pasos (feb 2026): la Constancia de Situación Fiscal (CSF) se pide DURANTE el
// registro, justo después de capturar los datos de la entidad. Antes se pedía después de
// entrar, en /onboarding, y no era obligatoria. Ahora es requisito para completar el alta.
const STEPS = [
  "Cuenta del usuario principal",
  "Alta de entidad",
  "Constancia fiscal",
  "Oficial de Cumplimiento",
  "Plan y suscripción",
];

// Aviso legal del tratamiento de la CSF, provisto por Servicios Legales y Fiscales BLC, S.C.
// (responsable). Oct-2026: la URL del aviso integral ya existe (/aviso-privacidad, versión
// 2026-10-16); el enlace se ofrece debajo de este texto, junto al checkbox.
const AVISO_PRIVACIDAD_CSF = `Servicios Legales y Fiscales BLC, S.C., que opera bajo la marca FELVORA, con domicilio en Avenida Tlaxcala Sur 36 B, Papalotla de Xicohténcatl, Tlaxcala, C.P. 90790, es responsable del tratamiento de los datos personales y fiscales contenidos en la Constancia de Situación Fiscal (RFC, denominación o razón social, domicilio fiscal, régimen y obligaciones), con la finalidad de validar la identidad de la entidad y dar de alta el expediente del sujeto obligado.

Los datos se resguardan conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y no se comparten con terceros sin consentimiento, salvo en los supuestos del artículo 37 de la Ley o requerimiento fundado de autoridad competente. Para ejercer derechos ARCO escriba a contacto@felvora.mx.`;

// Formatos y tamaño máximo permitidos para la CSF (tope único del sistema, lib/archivos.js).
const CSF_MAX_MB = MAX_MB;
const CSF_ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];

const planPrecioTxt = (p) => p.es_cotizacion
  ? "Cotización"
  : `$${Number(p.precio_mensual || 0).toLocaleString("es-MX")}/mes`;
// sep-2026: la unidad se nombra como lo que significa — 1 ALTA de cliente nuevo con consulta real de listas.
const planDescTxt = (p) => {
  if (p.es_cotizacion)
    return "Altas de clientes ilimitadas · usuarios ilimitados";
  const parts = [];
  const c = p.creditos_incluidos;
  if (c === -1) {
    parts.push("altas de clientes ilimitadas");
  } else if (c !== undefined && c !== null) {
    const n = Number(c).toLocaleString("es-MX");
    const extra = p.precio_credito_adicional
      ? ` (+$${Number(p.precio_credito_adicional).toLocaleString("es-MX")}/alta adicional)`
      : "";
    parts.push(`${n} altas de clientes incluidas/mes${extra}`);
  }
  parts.push("usuarios ilimitados");
  return parts.join(" · ");
};

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

export default function RegisterWizard() {
  const { registerEntity } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ago-2026 · el régimen sale del CATÁLOGO del backend, no de una lista escrita a mano.
  // `?regimen=` acepta el id o el nombre del catálogo. Si llega un valor que NO existe o
  // que está inactivo, se AVISA y NO se preselecciona nada: antes caía a "SOFOM ENR" en
  // silencio y una actividad vulnerable acababa registrada como entidad financiera.
  const [regimenes, setRegimenes] = useState([]);
  const [regimenAviso, setRegimenAviso] = useState("");
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [planes, setPlanes] = useState([]);
  const [usuario, setUsuario] = useState({ nombre: "", email: "", telefono: "", password: "", confirm: "", puesto: "Representante legal", terminos: false, privacidad: false, csf_privacidad: false });
  const [entidad, setEntidad] = useState({ tipo_persona: "Persona moral", razon_social: "", nombre_comercial: "", rfc: "", regimen: "SOFOM ENR", domicilio: "", telefono: "", correo: "", sitio_web: "", objeto_social: "" });
  const [oficial, setOficial] = useState({ nombre: "", rfc: "", curp: "", correo: "", telefono: "", cargo: "Oficial de Cumplimiento", tipo: "Propietario", certificacion_cnbv: "No" });
  const [plan, setPlan] = useState("profesional");
  const [codigo, setCodigo] = useState("");
  // CSF (Fase feb-2026): archivo obligatorio. Se sube al enviar el formulario junto al payload.
  // Vive en memoria (File object). Si el usuario refresca la página, se pierde por diseño.
  const [csfFile, setCsfFile] = useState(null); // File | null
  const [csfError, setCsfError] = useState("");
  const csfInputRef = useRef(null);
  // Turnstile — captcha del registro público. Solo se muestra en el último paso, junto al
  // botón submit, para no ensuciar la UX del wizard con el widget desde el paso 0.
  const [cfToken, setCfToken] = useState("");
  const turnstileRef = useRef(null);

  useEffect(() => {
    api.get("/planes/registro").then((r) => setPlanes(r.data)).catch(() => {});
  }, []);
  // Catálogo de regímenes: solo los ACTIVOS llegan al select. Los no habilitados quedan
  // ocultos, así nadie puede registrarse en algo que todavía no funciona.
  useEffect(() => {
    cargarRegimenes().then(({ financieras, vulnerables }) => {
      const activos = [...financieras, ...vulnerables].filter((r) => r.activo);
      setRegimenes(activos);
      const q = (searchParams.get("regimen") || "").trim();
      if (!q) return;
      const hit = buscarRegimen(activos, q);
      if (hit) {
        setEntidad((p) => ({ ...p, regimen: hit.nombre }));
      } else {
        // No se preselecciona nada: se limpia el default para que `validateStep` bloquee
        // el avance hasta que la persona elija de la lista. Antes esto caía en SOFOM ENR.
        setEntidad((p) => ({ ...p, regimen: "" }));
        setRegimenAviso(
          `El enlace traía el régimen “${q}”, que no existe en el catálogo o todavía no está ` +
          `habilitado. Elige tu régimen de la lista para continuar.`);
      }
    }).catch(() => {});
  }, [searchParams]);
  const _norm = (s) => String(s || "").toLowerCase();
  const regimenSel = regimenes.find((r) => _norm(r.nombre) === _norm(entidad.regimen)) || null;
  const planesFiltrados = planes.filter((p) => {
    const regs = (p.regimenes || []).map(_norm);
    if (regs.length === 0) return true;  // planes sin regimenes se muestran a todos (legacy)
    return regs.includes(_norm(entidad.regimen))
      || (!!regimenSel && regs.includes(_norm(regimenSel.id)));
  });
  // Cuando el régimen elegido solo tiene un plan (caso de las 17 actividades vulnerables,
  // que comparten el plan único del Art. 17), se selecciona SOLO, sin paso manual.
  useEffect(() => {
    if (planesFiltrados.length && !planesFiltrados.some((p) => p.id === plan)) {
      setPlan(planesFiltrados[0].id);
    }
  }, [entidad.regimen, planes]);  // eslint-disable-line react-hooks/exhaustive-deps


  const u = (k, v) => setUsuario((p) => ({ ...p, [k]: v }));
  const e = (k, v) => setEntidad((p) => ({ ...p, [k]: v }));
  const o = (k, v) => setOficial((p) => ({ ...p, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!usuario.nombre || !usuario.email || !usuario.password) return "Completa nombre, correo y contraseña.";
      if (usuario.password.length < 6) return "La contraseña debe tener al menos 6 caracteres.";
      if (usuario.password !== usuario.confirm) return "Las contraseñas no coinciden.";
      if (!usuario.terminos || !usuario.privacidad) return "Debes aceptar términos y aviso de privacidad.";
    }
    if (step === 1) {
      if (!entidad.razon_social || !entidad.rfc) return "Completa razón social y RFC.";
      if (!regimenSel) {
        return "Elige tu régimen de la lista. Solo se pueden registrar los regímenes habilitados.";
      }
    }
    // Paso 2 (Constancia fiscal, feb 2026): archivo + checkbox de privacidad son requisitos.
    if (step === 2) {
      if (!csfFile) return "Sube la Constancia de Situación Fiscal para continuar.";
      if (!usuario.csf_privacidad) return "Debes aceptar el aviso de privacidad para el tratamiento de la CSF.";
    }
    // Paso 3 (Oficial de Cumplimiento, feb 2026): nombre y correo son OBLIGATORIOS
    // por Art. 20 CUB (SOFOM ENR designa OC al constituirse). Además, correo distinto
    // del administrador — segregación de funciones.
    if (step === 3) {
      const ocNombre = (oficial.nombre || "").trim();
      const ocCorreo = (oficial.correo || "").trim().toLowerCase();
      const adminCorreo = (usuario.email || "").trim().toLowerCase();
      if (!ocNombre) return "Captura el nombre del Oficial de Cumplimiento (obligatorio).";
      if (!ocCorreo) return "Captura el correo del Oficial de Cumplimiento (obligatorio).";
      const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailRe.test(ocCorreo)) return "El correo del Oficial de Cumplimiento no tiene un formato válido.";
      if (adminCorreo && ocCorreo === adminCorreo) {
        return "El Oficial de Cumplimiento debe ser una persona distinta del administrador (segregación de funciones).";
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    if (!cfToken) {
      toast.error("Por favor completa la verificación de seguridad antes de continuar.");
      return;
    }
    // Cinturón y tirantes (feb 2026): defiende el candado por si el usuario llegara al Paso 4
    // saltando el 2. Sin CSF y sin consentimiento CSF, el registro NO se completa.
    if (!csfFile) {
      toast.error("Falta subir la Constancia de Situación Fiscal. Regresa al paso 3.");
      setStep(2);
      return;
    }
    if (!usuario.csf_privacidad) {
      toast.error("Falta aceptar el aviso de privacidad para el tratamiento de la CSF.");
      setStep(2);
      return;
    }
    setLoading(true);
    const cod = codigo.trim().toUpperCase();
    const res = await registerEntity(
      { usuario, entidad, oficial, plan, codigo: cod, cf_turnstile_token: cfToken },
      csfFile,
    );
    setLoading(false);
    try { turnstileRef.current?.reset(); } catch (_e) { /* no-op */ }
    setCfToken("");
    if (res.ok) {
      // feb-2026 iter 07-24l — El registro ya no devuelve access_token. Redirigimos a la
      // pantalla explicativa que ofrece reenviar el correo, para que el usuario NO se quede
      // frustrado buscando por qué no puede entrar (era el hueco del "primer login OK,
      // segundo login falla").
      const emailEncoded = encodeURIComponent(res.email || usuario.email || "");
      const estatusEncoded = encodeURIComponent(res.entity_estatus || "");
      // ago-2026 · el alta informa si el correo salió DE VERDAD. Si Brevo no lo aceptó, la
      // siguiente pantalla lo dice en rojo en vez de fingir que el enlace va en camino.
      const envio = res.verificacion_enviada === false ? "&envio=fallido" : "";
      navigate(`/verificar-correo-pendiente?email=${emailEncoded}&entity_estatus=${estatusEncoded}${envio}`);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="mb-8 flex items-center justify-between">
          <VoraLogo variant="horizontal" tone="dark" />
          <p className="hidden text-xs text-slate-500 sm:block">Alta de sujeto obligado</p>
        </div>

        {/* Stepper */}
        <div className="mb-8 flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={i} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors ${
                  i < step ? "border-primary bg-primary text-white" : i === step ? "border-accent bg-accent text-white" : "border-slate-200 bg-white text-slate-400"
                }`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
              </div>
              {i < STEPS.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < step ? "bg-primary" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-5 font-heading text-xl font-semibold text-slate-900">{STEPS[step]}</h2>

          {step === 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nombre completo" full><Input data-testid="reg-nombre" value={usuario.nombre} onChange={(ev) => u("nombre", ev.target.value)} /></Field>
              <Field label="Correo electrónico"><Input data-testid="reg-email" type="email" value={usuario.email} onChange={(ev) => u("email", ev.target.value)} /></Field>
              <Field label="Teléfono"><Input value={usuario.telefono} onChange={(ev) => u("telefono", ev.target.value)} /></Field>
              <Field label="Contraseña"><Input data-testid="reg-password" type="password" value={usuario.password} onChange={(ev) => u("password", ev.target.value)} /></Field>
              <Field label="Confirmar contraseña"><Input data-testid="reg-confirm" type="password" value={usuario.confirm} onChange={(ev) => u("confirm", ev.target.value)} /></Field>
              <Field label="Puesto o carácter" full>
                <Select value={usuario.puesto} onValueChange={(v) => u("puesto", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Representante legal", "Administrador", "Persona física titular", "Otro"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2 space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  {/* Oct-2026 · texto legal completo publicado en /terminos-y-condiciones
                       (versión 2026-10-16). Enlace en pestaña nueva para que la persona lo
                       lea antes de aceptar, sin perder el avance del registro. */}
                  <Checkbox data-testid="reg-terminos" checked={usuario.terminos} onCheckedChange={(v) => u("terminos", v)} />
                  <span>
                    Acepto los{" "}
                    <Link to="/terminos-y-condiciones" target="_blank" rel="noreferrer"
                      className="font-medium text-accent hover:underline" data-testid="reg-terminos-link">
                      términos y condiciones
                    </Link>.
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox data-testid="reg-privacidad" checked={usuario.privacidad} onCheckedChange={(v) => u("privacidad", v)} />
                  <span>
                    Acepto el{" "}
                    <Link to="/aviso-privacidad" target="_blank" rel="noreferrer"
                      className="font-medium text-accent hover:underline" data-testid="reg-privacidad-link">
                      aviso de privacidad
                    </Link>.
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Tipo de persona">
                <Select value={entidad.tipo_persona} onValueChange={(v) => e("tipo_persona", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Persona moral", "Persona física"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Régimen">
                {/*
                  ago-2026 · el select se llena con los regímenes ACTIVOS del catálogo del
                  backend (`/api/meta` → `regimenes`). Habilitar una fracción nueva la
                  publica aquí sola, sin desplegar frontend. Los no habilitados no se
                  muestran: nadie puede registrarse en algo que no funciona.
                  El giro se deriva server-side en /auth/register-entity.
                */}
                <Select value={entidad.regimen} onValueChange={(v) => { e("regimen", v); setRegimenAviso(""); }}>
                  <SelectTrigger data-testid="reg-regimen-select"><SelectValue placeholder="Elige tu régimen…" /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Entidades financieras</SelectLabel>
                      {regimenes.filter((r) => r.grupo === "financiera").map((r) => (
                        <SelectItem key={r.id} value={r.nombre} data-testid={`reg-regimen-opt-${r.id}`}>
                          {r.nombre}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Actividades Vulnerables · Art. 17 LFPIORPI</SelectLabel>
                      {regimenes.filter((r) => r.grupo === "actividad_vulnerable").map((r) => (
                        <SelectItem key={r.id} value={r.nombre} data-testid={`reg-regimen-opt-${r.id}`}>
                          {r.fraccion ? `Fr. ${r.fraccion_display || r.fraccion} · ` : ""}{r.nombre}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {regimenAviso && (
                  <p className="mt-1.5 text-xs font-medium text-red-600" data-testid="reg-regimen-aviso">
                    {regimenAviso}
                  </p>
                )}
              </Field>
              <Field label="Razón social / Nombre completo" full><Input data-testid="reg-razon" value={entidad.razon_social} onChange={(ev) => e("razon_social", ev.target.value)} /></Field>
              <Field label="Nombre comercial"><Input value={entidad.nombre_comercial} onChange={(ev) => e("nombre_comercial", ev.target.value)} /></Field>
              <Field label="RFC"><Input data-testid="reg-rfc" value={entidad.rfc} onChange={(ev) => e("rfc", ev.target.value)} /></Field>
              <Field label="Domicilio fiscal" full><Textarea value={entidad.domicilio} onChange={(ev) => e("domicilio", ev.target.value)} rows={2} /></Field>
              <Field label="Teléfono"><Input value={entidad.telefono} onChange={(ev) => e("telefono", ev.target.value)} /></Field>
              <Field label="Correo institucional"><Input value={entidad.correo} onChange={(ev) => e("correo", ev.target.value)} /></Field>
              <Field label="Sitio web"><Input value={entidad.sitio_web} onChange={(ev) => e("sitio_web", ev.target.value)} /></Field>
              <Field label="Objeto social" full><Textarea value={entidad.objeto_social} onChange={(ev) => e("objeto_social", ev.target.value)} rows={2} /></Field>
            </div>
          )}

          {step === 2 && (
            // Paso NUEVO (feb 2026): Constancia de Situación Fiscal + aviso legal + consentimiento.
            // Sin archivo válido y sin checkbox marcado, el usuario no avanza (validateStep).
            <div className="space-y-5" data-testid="reg-paso-csf">
              <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-heading text-sm font-semibold text-slate-900">Constancia de Situación Fiscal</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Sube la CSF vigente emitida por el SAT. Con este documento validamos los datos fiscales
                    de la entidad y damos de alta tu expediente. Es un requisito para completar el registro.
                  </p>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Formatos aceptados: {CSF_ALLOWED_EXT.join(", ").toUpperCase()} · Peso máximo: {CSF_MAX_MB} MB.
                  </p>
                </div>
              </div>

              {/* Selector de archivo */}
              {!csfFile ? (
                <div>
                  <input
                    ref={csfInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    className="hidden"
                    data-testid="reg-csf-file-input"
                    onChange={(ev) => {
                      const f = ev.target.files?.[0];
                      if (!f) return;
                      setCsfError("");
                      const ext = f.name.split(".").pop()?.toLowerCase() || "";
                      if (!CSF_ALLOWED_EXT.includes(ext)) {
                        setCsfError(`Formato no aceptado. Usa: ${CSF_ALLOWED_EXT.join(", ").toUpperCase()}.`);
                        return;
                      }
                      if (f.size > CSF_MAX_MB * 1024 * 1024) {
                        setCsfError(`El archivo pesa ${(f.size / (1024 * 1024)).toFixed(1)} MB; el máximo es ${CSF_MAX_MB} MB. Comprime o adjunta un archivo más ligero.`);
                        return;
                      }
                      setCsfFile(f);
                    }}
                  />
                  <button
                    type="button"
                    data-testid="reg-csf-btn-elegir"
                    onClick={() => csfInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-white px-4 py-8 text-sm font-medium text-slate-600 hover:border-primary hover:text-primary">
                    <Upload className="h-5 w-5" />
                    Elegir archivo…
                  </button>
                  {csfError && (
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                         data-testid="reg-csf-error">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{csfError}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
                     data-testid="reg-csf-file-cargado">
                  <FileText className="h-6 w-6 shrink-0 text-emerald-700" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-semibold text-emerald-900" data-testid="reg-csf-file-nombre">
                      {csfFile.name}
                    </p>
                    <p className="text-[11px] text-emerald-700">
                      {(csfFile.size / (1024 * 1024)).toFixed(2)} MB · Listo para subir.
                    </p>
                  </div>
                  <button type="button" data-testid="reg-csf-file-cambiar"
                    onClick={() => { setCsfFile(null); setCsfError(""); }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline">
                    <X className="h-3.5 w-3.5" /> Cambiar
                  </button>
                </div>
              )}

              {/* Aviso legal del tratamiento de la CSF + checkbox bloqueante */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="reg-csf-aviso-box">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Aviso de privacidad para la CSF
                </p>
                <div className="mb-3 max-h-40 overflow-y-auto whitespace-pre-line text-xs leading-relaxed text-slate-600"
                     data-testid="reg-csf-aviso-texto">
                  {AVISO_PRIVACIDAD_CSF}
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <Checkbox data-testid="reg-csf-privacidad" className="mt-0.5"
                    checked={usuario.csf_privacidad} onCheckedChange={(v) => u("csf_privacidad", v)} />
                  <span>
                    Acepto el aviso de privacidad para el tratamiento de mi Constancia de Situación Fiscal (CSF).
                    Consulta el{" "}
                    <Link to="/aviso-privacidad" target="_blank" rel="noreferrer"
                      className="font-medium text-accent hover:underline" data-testid="reg-csf-aviso-link">
                      aviso completo
                    </Link>.
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="reg-paso-oficial">
              <div className="sm:col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="reg-oficial-obligatorio-aviso">
                <b>Obligatorio (Art. 20 CUB / LFPIORPI):</b> nombre y correo del Oficial de Cumplimiento son requeridos. El Oficial debe ser una persona distinta del administrador que se está registrando (segregación de funciones).
              </div>
              <Field label="Nombre completo *" full><Input data-testid="reg-oficial-nombre" value={oficial.nombre} onChange={(ev) => o("nombre", ev.target.value)} /></Field>
              <Field label="RFC"><Input data-testid="reg-oficial-rfc" value={oficial.rfc} onChange={(ev) => o("rfc", ev.target.value)} /></Field>
              <Field label="CURP"><Input data-testid="reg-oficial-curp" value={oficial.curp} onChange={(ev) => o("curp", ev.target.value)} /></Field>
              <Field label="Correo *"><Input data-testid="reg-oficial-correo" type="email" value={oficial.correo} onChange={(ev) => o("correo", ev.target.value)} /></Field>
              <Field label="Teléfono"><Input data-testid="reg-oficial-telefono" value={oficial.telefono} onChange={(ev) => o("telefono", ev.target.value)} /></Field>
              <Field label="Cargo"><Input data-testid="reg-oficial-cargo" value={oficial.cargo} onChange={(ev) => o("cargo", ev.target.value)} /></Field>
              <Field label="Tipo">
                <Select value={oficial.tipo} onValueChange={(v) => o("tipo", v)}>
                  <SelectTrigger data-testid="reg-oficial-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Propietario", "Suplente"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="¿Certificación CNBV?">
                <Select value={oficial.certificacion_cnbv} onValueChange={(v) => o("certificacion_cnbv", v)}>
                  <SelectTrigger data-testid="reg-oficial-certificacion"><SelectValue /></SelectTrigger>
                  <SelectContent>{["No", "Sí"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {planesFiltrados.length === 1 && (
                <div className="sm:col-span-2 lg:col-span-4 rounded-lg border border-forest-600/30 bg-forest-600/5 p-3 text-sm text-forest-800"
                     data-testid="reg-plan-automatico">
                  Tu régimen <b>{regimenSel?.nombre || entidad.regimen}</b> tiene un plan asignado
                  automáticamente. No hay nada que elegir.
                </div>
              )}
              {planesFiltrados.map((p) => (
                <button key={p.id} type="button" data-testid={`plan-${p.id}`} onClick={() => setPlan(p.id)}
                  className={`rounded-lg border-2 p-4 text-left transition-all ${plan === p.id ? "border-accent bg-sage/15/50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="font-heading text-base font-semibold text-slate-900">{p.nombre}</p>
                  <p className="mt-1 font-heading text-xl font-bold text-primary">
                    {planPrecioTxt(p)}
                    {!p.es_cotizacion && (
                      <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-400"
                            data-testid={`precio-mas-iva-${p.id}`}>+ IVA</span>
                    )}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">{planDescTxt(p)}</p>
                  {plan === p.id && <p className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent"><Check className="h-3 w-3" /> Seleccionado</p>}
                </button>
              ))}
              <p className="sm:col-span-2 lg:col-span-4 text-xs text-slate-500">
                Pago seguro con Stripe (no almacenamos datos de tu tarjeta). Al continuar, crearemos tu cuenta y
                realizarás el pago de tu membresía para activarla; después cargarás tu documentación para revisión.
              </p>
              <p data-testid="reg-corporativo-contacto" className="sm:col-span-2 lg:col-span-4 text-xs text-slate-400">
                ¿Necesitas un plan Corporativo / Multi-entidad?{" "}
                <a href="mailto:hola@felvora.app?subject=Plan%20Corporativo%20FELVORA" className="font-medium text-accent hover:underline">
                  Contáctanos
                </a>{" "}para una cotización a la medida.
              </p>
              <div className="sm:col-span-2 lg:col-span-4 mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                  ¿Tienes un código de invitación? <span className="font-normal text-slate-400">(opcional)</span>
                </Label>
                <Input
                  data-testid="reg-codigo-cortesia"
                  value={codigo}
                  onChange={(ev) => setCodigo(ev.target.value.toUpperCase())}
                  placeholder="CORT-XXXX-XXXX o CONT-XXXX-XXXX"
                  className="max-w-xs font-mono tracking-wide"
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Si tienes un código de cortesía de FELVORA (CORT-…) o un código de paquete
                  de tu contador (CONT-…), ingrésalo para activar tu cuenta sin pago en línea.
                  Tú capturas tus propios datos: RFC, razón social y tu actividad vulnerable.
                </p>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" onClick={() => (step === 0 ? navigate("/login") : setStep((s) => s - 1))}>
              <ArrowLeft className="mr-1.5 h-4 w-4" /> {step === 0 ? "Volver a login" : "Anterior"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button data-testid="reg-next" onClick={next} className="bg-primary hover:bg-sidebar-hover">
                Continuar <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <div className="flex flex-col items-end gap-3">
                {/* Turnstile: captcha en español antes del submit del último paso. Ver Login.jsx. */}
                <div data-testid="registro-turnstile-wrapper">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    options={{ language: "es", theme: "light" }}
                    onSuccess={(token) => setCfToken(token)}
                    onExpire={() => setCfToken("")}
                    onError={() => setCfToken("")}
                  />
                </div>
                <Button data-testid="reg-submit" onClick={submit} disabled={loading || !cfToken}
                  className="bg-primary hover:bg-sidebar-hover">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {codigo.trim() ? "Aplicar código y continuar" : "Continuar al pago"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          ¿Ya tienes cuenta? <Link to="/login" className="font-medium text-accent hover:underline">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}
